import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Enums from '../Enums';
import { CargoCharacteristicIdentity } from './CargoCharacteristicEntity';

export interface IAlterationEntityAttributes extends IModelAttributes {
	status: Enums.bookingStatus;
	legacyId: number;
	note: string;
	isInvoiced: boolean;
	isUnpaid: boolean;

	bookingId: string;
	booking: Models.BookingEntity | Models.IBookingEntityAttributes;
	discounts: Array<Models.DiscountAlteration | Models.IDiscountAlterationAttributes>;
	eventBookingDetailId?: string;
	eventBookingDetail?: Models.EventBookingDetailEntity | Models.IEventBookingDetailEntityAttributes;
	ferryTripId?: string;
	ferryTrip?: Models.FerryTripEntity | Models.IFerryTripEntityAttributes;
	transactionId: string;
	transaction: Models.TransactionEntity | Models.ITransactionEntityAttributes;
	endStopId?: string;
	endStop?: Models.StopEntity | Models.IStopEntityAttributes;
	fees: Array<Models.FeeEntity | Models.IFeeEntityAttributes>;
	managerBookingDiscountUsages: Array<
		Models.ManagerBookingDiscountUsagesEntity | Models.IManagerBookingDiscountUsagesEntityAttributes
	>;
	managerBookingDiscounts: Array<
		Models.ManagerBookingDiscountEntity | Models.IManagerBookingDiscountEntityAttributes
	>;
	startStopId?: string;
	startStop?: Models.StopEntity | Models.IStopEntityAttributes;
	tickets: Array<Models.TicketEntity | Models.ITicketEntityAttributes>;
	alterationEntitlementRedemptions: Array<
		Models.AlterationEntitlementRedemptionEntity | Models.IAlterationEntitlementRedemptionEntityAttributes
	>;
}
@entity('AlterationEntity', 'Alteration')
export default class AlterationEntity extends Model implements IAlterationEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<AlterationEntity, Enums.bookingStatus>()
	public status: Enums.bookingStatus;

	@observable
	@attribute<AlterationEntity, number>()
	public legacyId: number;

	/**
	 * Booking additional details
	 */
	@observable
	@attribute<AlterationEntity, string>()
	public note: string;

	/**
	 * If the alteration has been invoiced or not.
	 */
	@observable
	@attribute<AlterationEntity, boolean>()
	public isInvoiced: boolean;

	@observable
	@attribute<AlterationEntity, boolean>()
	public isUnpaid: boolean;

	@observable
	@attribute()
	public bookingId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public booking: Models.BookingEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public discounts: Models.DiscountAlteration[] = [];

	@observable
	@attribute()
	public eventBookingDetailId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public eventBookingDetail: Models.EventBookingDetailEntity;

	/**
	 * The relationship between an alteration and a ferry trip
	 */
	@observable
	@attribute()
	public ferryTripId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public ferryTrip: Models.FerryTripEntity;

	@observable
	@attribute()
	public transactionId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public transaction: Models.TransactionEntity;

	@observable
	@attribute()
	public endStopId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public endStop: Models.StopEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public fees: Models.FeeEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public managerBookingDiscountUsages: Models.ManagerBookingDiscountUsagesEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public managerBookingDiscounts: Models.ManagerBookingDiscountEntity[] = [];

	@observable
	@attribute()
	public startStopId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public startStop: Models.StopEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public tickets: Models.TicketEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public alterationEntitlementRedemptions: Models.AlterationEntitlementRedemptionEntity[] = [];

	constructor(attributes?: Partial<IAlterationEntityAttributes>) {
		super(attributes);

		this.fees = [];
		this.tickets = [];
		this.managerBookingDiscountUsages = [];
		this.managerBookingDiscounts = [];
		this.discounts = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IAlterationEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.status !== undefined) {
				this.status = attributes.status;
			}
			if (attributes.legacyId !== undefined) {
				this.legacyId = attributes.legacyId;
			}
			if (attributes.note !== undefined) {
				this.note = attributes.note;
			}
			if (attributes.isInvoiced !== undefined) {
				this.isInvoiced = attributes.isInvoiced;
			}
			if (attributes.isUnpaid !== undefined) {
				this.isUnpaid = attributes.isUnpaid;
			}
			if (attributes.bookingId !== undefined) {
				this.bookingId = attributes.bookingId;
			}
			if (attributes.booking !== undefined) {
				if (attributes.booking === null) {
					this.booking = attributes.booking;
				} else if (attributes.booking instanceof Models.BookingEntity) {
					this.booking = attributes.booking;
					this.bookingId = attributes.booking.id;
				} else {
					this.booking = new Models.BookingEntity(attributes.booking);
					this.bookingId = this.booking.id;
				}
			}
			if (attributes.discounts !== undefined && Array.isArray(attributes.discounts)) {
				if (!this.discounts) {
					this.discounts = [];
				}
				for (const model of attributes.discounts) {
					if (model instanceof Models.DiscountAlteration) {
						this.discounts.push(model);
					} else {
						this.discounts.push(new Models.DiscountAlteration(model));
					}
				}
			}
			if (attributes.eventBookingDetailId !== undefined) {
				this.eventBookingDetailId = attributes.eventBookingDetailId;
			}
			if (attributes.eventBookingDetail !== undefined) {
				if (attributes.eventBookingDetail === null) {
					this.eventBookingDetail = attributes.eventBookingDetail;
				} else if (attributes.eventBookingDetail instanceof Models.EventBookingDetailEntity) {
					this.eventBookingDetail = attributes.eventBookingDetail;
					this.eventBookingDetailId = attributes.eventBookingDetail.id;
				} else {
					this.eventBookingDetail = new Models.EventBookingDetailEntity(attributes.eventBookingDetail);
					this.eventBookingDetailId = this.eventBookingDetail.id;
				}
			}
			if (attributes.ferryTripId !== undefined) {
				this.ferryTripId = attributes.ferryTripId;
			}
			if (attributes.ferryTrip !== undefined) {
				if (attributes.ferryTrip === null) {
					this.ferryTrip = attributes.ferryTrip;
				} else if (attributes.ferryTrip instanceof Models.FerryTripEntity) {
					this.ferryTrip = attributes.ferryTrip;
					this.ferryTripId = attributes.ferryTrip.id;
				} else {
					this.ferryTrip = new Models.FerryTripEntity(attributes.ferryTrip);
					this.ferryTripId = this.ferryTrip.id;
				}
			}
			if (attributes.transactionId !== undefined) {
				this.transactionId = attributes.transactionId;
			}
			if (attributes.transaction !== undefined) {
				if (attributes.transaction === null) {
					this.transaction = attributes.transaction;
				} else if (attributes.transaction instanceof Models.TransactionEntity) {
					this.transaction = attributes.transaction;
					this.transactionId = attributes.transaction.id;
				} else {
					this.transaction = new Models.TransactionEntity(attributes.transaction);
					this.transactionId = this.transaction.id;
				}
			}
			if (attributes.endStopId !== undefined) {
				this.endStopId = attributes.endStopId;
			}
			if (attributes.endStop !== undefined) {
				if (attributes.endStop === null) {
					this.endStop = attributes.endStop;
				} else if (attributes.endStop instanceof Models.StopEntity) {
					this.endStop = attributes.endStop;
					this.endStopId = attributes.endStop.id;
				} else {
					this.endStop = new Models.StopEntity(attributes.endStop);
					this.endStopId = this.endStop.id;
				}
			}
			if (attributes.fees !== undefined && Array.isArray(attributes.fees)) {
				if (!this.fees) {
					this.fees = [];
				}
				for (const model of attributes.fees) {
					if (model instanceof Models.FeeEntity) {
						this.fees.push(model);
					} else {
						this.fees.push(new Models.FeeEntity(model));
					}
				}
			}
			if (
				attributes.managerBookingDiscountUsages !== undefined &&
				Array.isArray(attributes.managerBookingDiscountUsages)
			) {
				if (!this.managerBookingDiscountUsages) {
					this.managerBookingDiscountUsages = [];
				}
				for (const model of attributes.managerBookingDiscountUsages) {
					if (model instanceof Models.ManagerBookingDiscountUsagesEntity) {
						this.managerBookingDiscountUsages.push(model);
					} else {
						this.managerBookingDiscountUsages.push(new Models.ManagerBookingDiscountUsagesEntity(model));
					}
				}
			}
			if (attributes.managerBookingDiscounts !== undefined && Array.isArray(attributes.managerBookingDiscounts)) {
				if (!this.managerBookingDiscounts) {
					this.managerBookingDiscounts = [];
				}
				for (const model of attributes.managerBookingDiscounts) {
					if (model instanceof Models.ManagerBookingDiscountEntity) {
						this.managerBookingDiscounts.push(model);
					} else {
						this.managerBookingDiscounts.push(new Models.ManagerBookingDiscountEntity(model));
					}
				}
			}
			if (attributes.startStopId !== undefined) {
				this.startStopId = attributes.startStopId;
			}
			if (attributes.startStop !== undefined) {
				if (attributes.startStop === null) {
					this.startStop = attributes.startStop;
				} else if (attributes.startStop instanceof Models.StopEntity) {
					this.startStop = attributes.startStop;
					this.startStopId = attributes.startStop.id;
				} else {
					this.startStop = new Models.StopEntity(attributes.startStop);
					this.startStopId = this.startStop.id;
				}
			}
			if (attributes.tickets !== undefined && Array.isArray(attributes.tickets)) {
				if (!this.tickets) {
					this.tickets = [];
				}
				for (const model of attributes.tickets) {
					if (model instanceof Models.TicketEntity) {
						this.tickets.push(model);
					} else {
						this.tickets.push(new Models.TicketEntity(model));
					}
				}
			}
		}
	}
}

export interface AlterationContext {
	cargoCharacteristics: CargoCharacteristicIdentity[];
	/** Cargo characteristics selected by the customer for the trailer (tow-on) on this alteration. */
	trailerCargoCharacteristics?: CargoCharacteristicIdentity[];
	/** Inherited vehicle characteristics (from the cargo type) the customer removed for this booking. */
	removedInheritedCharacteristics?: CargoCharacteristicIdentity[];
	/** Values entered for admin-configured dynamic booking fields, keyed by field id. */
	dynamicFieldValues?: Array<{ fieldId: string; value: string }>;
}

export const getCharacteristicIds = (items: CargoCharacteristicIdentity[] = []) => {
	return items.map(x => x.characteristicId);
};
